import React, { useState, useEffect, useRef } from "react";
import { useAuth } from '../../contexts/AuthContext';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { LogOut, Menu, X, ChevronLeft, ChevronRight, Bell } from 'lucide-react';
import EnergyRingAvatar from '../../assets/frameAnimation/EnergyRingAvatar';
import { isProUser } from '../../services/coachVoice.service';
import BrandLogo from '../common/BrandLogo';
import NotificationBell from '../notification/NotificationBell';
import { useNotification } from '../../contexts/NotificationContext';
import "./Vertical-Nav.css";

const VerticalNav = ({ isNavOpen, onCollapseChange }) => {
  const { currentUser: user, logout } = useAuth();
  const { unreadCount } = useNotification();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const navRef = useRef(null);
  const hamburgerRef = useRef(null);

  const handleLogout = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await logout();
      navigate('/login');
    } catch (err) {
      console.error('Logout error:', err);
      navigate('/login');
    }
  };

  // Notify parent component when collapse state changes
  useEffect(() => {
    if (onCollapseChange) {
      onCollapseChange(isCollapsed);
    }
  }, [isCollapsed, onCollapseChange]);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location]);

  // Close mobile menu on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        mobileMenuOpen &&
        navRef.current &&
        !navRef.current.contains(event.target) &&
        hamburgerRef.current &&
        !hamburgerRef.current.contains(event.target)
      ) {
        setMobileMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [mobileMenuOpen]);

  return (
    <>
      {/* Mobile menu overlay */}
      {mobileMenuOpen && (
        <div 
          className="mobile-menu-overlay" 
          onClick={() => setMobileMenuOpen(false)}
        />
      )}
      
      <aside 
        className={`vertical-nav ${isNavOpen ? 'open' : ''} ${isCollapsed ? 'collapsed' : ''} ${mobileMenuOpen ? 'mobile-menu-active' : ''}`}
      >
        <div className={`nav-header ${(location.pathname === '/home' || location.pathname === '/') ? 'active' : ''}`}>
          {/* Hamburger menu for mobile */}
          {!mobileMenuOpen && (
            <button
              ref={hamburgerRef}
              className="hamburger-menu"
              aria-label="Open menu"
              onClick={() => setMobileMenuOpen(true)}
            >
              <Menu size={24} className="text-white" />
            </button>
          )}

          <div className="nav-header-content">
            {/* Collapse/Expand toggle for desktop */}
            <button
              className="collapse-toggle"
              aria-label={isCollapsed ? "Expand menu" : "Collapse menu"}
              onClick={() => setIsCollapsed((prev) => !prev)}
            >
              {isCollapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
            </button>
            
            <div className="logo-section">
              <Link to="/home" className="logo-link">
                {user?.academy_branding?.academy_logo_url ? (
                  <img src={user.academy_branding.academy_logo_url} alt={user.academy_branding.academy_name} style={{ maxHeight: '44px', height: '38px', width: 'auto', objectFit: 'contain' }} />
                ) : user?.academy_branding?.academy_name ? (
                  <div style={{ color: '#fff', fontWeight: '800', fontSize: '15px', padding: '4px 0', lineHeight: '1.2' }}>
                    {user.academy_branding.academy_name}
                  </div>
                ) : (
                  <BrandLogo collapsed={isCollapsed} size="default" showIcon={true} />
                )}
              </Link>
            </div>
          </div>
        </div>

        <nav ref={navRef} className={`nav-links ${mobileMenuOpen ? 'show' : ''}`}>
          <div className={`mobile-only-logo ${(location.pathname === '/home' || location.pathname === '/') ? 'active' : ''}`}>
            <Link to="/home" onClick={() => setMobileMenuOpen(false)} className={(location.pathname === '/home' || location.pathname === '/') ? 'active' : ''}>
              {user?.academy_branding?.academy_logo_url ? (
                <img src={user.academy_branding.academy_logo_url} alt={user.academy_branding.academy_name} style={{ maxHeight: '44px', height: '38px', width: 'auto', objectFit: 'contain' }} />
              ) : user?.academy_branding?.academy_name ? (
                <div style={{ color: '#fff', fontWeight: '800', fontSize: '15px' }}>
                  {user.academy_branding.academy_name}
                </div>
              ) : (
                <BrandLogo size="default" showIcon={true} />
              )}
            </Link>
          </div>
          <Link to="/board-editor" onClick={() => setMobileMenuOpen(false)} className={`nav-btn flex items-center gap-4 ${location.pathname.startsWith('/board-editor') ? 'active' : ''}`}>
            <img 
              src="/images/icon/editor.webp?v=2" 
              alt="Editor" 
              className="w-12 h-12" 
              onError={(e) => {
                if (!e.target.dataset.retried) {
                  e.target.dataset.retried = 'true';
                  e.target.src = '/images/icon/editor.webp?t=' + Date.now();
                }
              }}
            />
            <span className="font-bold">Editor</span>
          </Link>
          <Link to="/analysis" onClick={() => setMobileMenuOpen(false)} className={`nav-btn flex items-center gap-4 ${location.pathname.startsWith('/analysis') || location.pathname.startsWith('/game-viewer') ? 'active' : ''}`}>
            <img 
              src="/images/icon/analysis.webp?v=2" 
              alt="Analysis" 
              className="w-12 h-12" 
              onError={(e) => {
                if (!e.target.dataset.retried) {
                  e.target.dataset.retried = 'true';
                  e.target.src = '/images/icon/analysis.webp?t=' + Date.now();
                }
              }}
            />
            <span className="font-bold">Analysis</span>
          </Link>
          <Link to="/play-with-friend" onClick={() => setMobileMenuOpen(false)} className={`nav-btn flex items-center gap-4 ${location.pathname.startsWith('/play-with-friend') ? 'active' : ''}`}>
            <img 
              src="/images/icon/play.webp?v=2" 
              alt="Play with friend" 
              className="w-12 h-12" 
              onError={(e) => {
                if (!e.target.dataset.retried) {
                  e.target.dataset.retried = 'true';
                  e.target.src = '/images/icon/play.webp?t=' + Date.now();
                }
              }}
            />
            <span className="font-bold">Play Live</span>
          </Link>

          <Link to="/lessons" onClick={() => setMobileMenuOpen(false)} className={`nav-btn flex items-center gap-4 ${location.pathname.startsWith('/lesson') ? 'active' : ''}`}>
            <img 
              src="/images/skills/knight.webp" 
              alt="Lessons" 
              className="w-12 h-12 object-contain" 
              onError={(e) => {
                if (!e.target.dataset.retried) {
                  e.target.dataset.retried = 'true';
                  e.target.src = '/images/skills/knight.png';
                }
              }}
            />
            <span className="font-bold">Lessons</span>
          </Link>

          <Link to="/endgame" onClick={() => setMobileMenuOpen(false)} className={`nav-btn flex items-center gap-4 ${location.pathname.startsWith('/endgame') || location.pathname.startsWith('/combat-labs') ? 'active' : ''}`}>
            <img 
              src="/images/icon/labs.webp?v=2" 
              alt="Endgame" 
              className="w-12 h-12" 
              onError={(e) => {
                if (!e.target.dataset.retried) {
                  e.target.dataset.retried = 'true';
                  e.target.src = '/images/icon/labs.webp?t=' + Date.now();
                }
              }}
            />
            <span className="font-bold">Endgame</span>
          </Link>
          <Link to="/tactics" onClick={() => setMobileMenuOpen(false)} className={`nav-btn flex items-center gap-4 ${location.pathname.startsWith('/tactics') || location.pathname.startsWith('/learn') ? 'active' : ''}`}>
            <img 
              src="/images/badges/mission-driven.webp" 
              alt="Tactics" 
              className="w-12 h-12 object-contain" 
              onError={(e) => {
                if (!e.target.dataset.retried) {
                  e.target.dataset.retried = 'true';
                  e.target.src = '/images/badges/mission-driven.webp?t=' + Date.now();
                }
              }}
            />
            <span className="font-bold">Tactics</span>
          </Link>
          <Link to="/puzzles" onClick={() => setMobileMenuOpen(false)} className={`nav-btn flex items-center gap-4 ${location.pathname.startsWith('/puzzle') ? 'active' : ''}`}>
            <img 
              src="/images/icon/puzzle.webp?v=2" 
              alt="Puzzles" 
              className="w-12 h-12" 
              onError={(e) => {
                if (!e.target.dataset.retried) {
                  e.target.dataset.retried = 'true';
                  e.target.src = '/images/icon/puzzle.webp?t=' + Date.now();
                }
              }}
            />
            <span className="font-bold">Puzzles</span>
          </Link>
          <Link to="/classroom" onClick={() => setMobileMenuOpen(false)} className={`nav-btn flex items-center gap-4 ${location.pathname.startsWith('/classroom') ? 'active' : ''}`}>
            <img 
              src="/images/icon/classroom.webp?v=2" 
              alt="Classroom" 
              className="w-12 h-12" 
              onError={(e) => {
                if (!e.target.dataset.retried) {
                  e.target.dataset.retried = 'true';
                  e.target.src = '/images/icon/classroom.webp?t=' + Date.now();
                }
              }}
            />
            <span className="font-bold">Classroom</span>
          </Link>

          <Link to="/friends" onClick={() => setMobileMenuOpen(false)} className={`nav-btn flex items-center gap-4 ${location.pathname.startsWith('/friends') ? 'active' : ''}`}>
            <img 
              src="/images/icon/friends.webp?v=2" 
              alt="Friends" 
              className="w-12 h-12" 
              onError={(e) => {
                if (!e.target.dataset.retried) {
                  e.target.dataset.retried = 'true';
                  e.target.src = '/images/icon/friends.webp?t=' + Date.now();
                }
              }}
            />
            <span className="font-bold">Friends</span>
          </Link>
          <Link to="/blogs" onClick={() => setMobileMenuOpen(false)} className={`nav-btn flex items-center gap-4 ${location.pathname.startsWith('/blog') ? 'active' : ''}`}>
            <div className="w-12 h-12 flex items-center justify-center shrink-0">
              <img 
                src="/images/blog.png" 
                alt="Blogs" 
                className="w-8 h-8 object-contain" 
                onError={(e) => {
                  if (!e.target.dataset.retried) {
                    e.target.dataset.retried = 'true';
                    e.target.src = '/images/blog.png?t=' + Date.now();
                  }
                }}
              />
            </div>
            <span className="font-bold">Blogs</span>
          </Link>

          {user && (
            <Link 
              to="/notifications" 
              onClick={() => setMobileMenuOpen(false)} 
              className={`nav-btn flex items-center gap-4 ${location.pathname.startsWith('/notifications') ? 'active' : ''}`}
            >
              <div className="w-12 h-12 flex items-center justify-center shrink-0">
                <div className="w-8 h-8 rounded-xl bg-amber-500/15 border border-amber-500/30 flex items-center justify-center text-amber-400">
                  <Bell size={18} />
                </div>
              </div>
              <span className="font-bold flex items-center justify-between flex-1 pr-2">
                <span>Notifications</span>
                {unreadCount > 0 && (
                  <span className="px-2 py-0.5 text-[11px] font-extrabold bg-red-500 text-white rounded-full">
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                )}
              </span>
            </Link>
          )}
        </nav>

        <div className={`auth-section ${mobileMenuOpen ? 'show' : ''} ${!user ? 'has-login-btn' : ''} ${user && location.pathname.startsWith('/profile') ? 'active' : ''}`}>
          {!user ? (
            <button 
              className="login-btn-home" 
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setMobileMenuOpen(false);
                navigate('/login');
              }}
            >
              Login
            </button>
          ) : (
            <div className="user-section-wrapper flex items-center justify-between w-full">
              <Link
                to="/profile"
                className="user-menu-button flex items-center gap-3 flex-1 min-w-0 overflow-visible"
                onClick={() => setMobileMenuOpen(false)}
              >
                <div className="user-avatar-nav flex items-center justify-center shrink-0 overflow-visible">
                  {user.profile_image_url ? (
                    isProUser(user) ? (
                      <EnergyRingAvatar
                        image={user.profile_image_url}
                        size={32}
                        name={user.username || "User"}
                        isPro={true}
                        theme="purple"
                      />
                    ) : (
                      <img 
                        src={user.profile_image_url} 
                        alt={user.username || "User"} 
                        className="w-full h-full rounded-xl object-cover"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                    )
                  ) : null}
                  <span 
                    className="w-full h-full flex items-center justify-center text-white font-bold"
                    style={{ display: user.profile_image_url ? 'none' : 'flex' }}
                  >
                    {user.username ? user.username[0].toUpperCase() : "?"}
                  </span>
                </div>
                {!isCollapsed && (
                  <span className="user-name font-bold truncate">{user.username || "User"}</span>
                )}
              </Link>
              <div className="flex items-center gap-2 shrink-0">
                <NotificationBell isCollapsed={isCollapsed} />
                {!isCollapsed && (
                  <button
                    className="nav-logout-btn"
                    onClick={handleLogout}
                    title="Logout"
                    aria-label="Logout"
                  >
                    <LogOut size={18} />
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </aside>

    </>
  );
};

export default VerticalNav;
